import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Container,
  ContentWrapper,
  ReturnButton,
  SubmitButton,
  LoadingOverlay,
  ChatBubbleContainer,
  ChatText,
  LogoImage,
  OptionButton,
  LogoBubbleWrapper
} from './styles'; // Réutilisation des styles de Lycee

// Import des utilitaires de recherche du Comparateur
import { optimizedSearch, loadSearchData } from '../Comparateur/search-utils';
import formationsMeta from "../Comparateur/formationsMeta.json"; // Added for optimizedSearch
import searchIndex from "../Comparateur/searchIndex.json"; // Added for optimizedSearch
import apiCall from '../../utils/api';
import ProgressBar from '../ProgressBar';
import useDeviceType from '../useDeviceType';
import { useAuth } from '../../contexts/AuthContext';
import SearchInputComponent from '../shared/SearchInputComponent';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png'; // Added import for back button image

// Import des assets
import compassImage from '../../assets/meo_write.png';

// Conteneur de recherche inspiré du Comparateur
const SearchBox = styled.div`
  padding: 16px 0;
  margin-bottom: 16px;
  position: relative;
  z-index: 100;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  margin-top: 20px;
`;

// Conteneur des résultats de formation
const FormationResultsContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
  display: ${props => props.visible ? 'block' : 'none'};
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  width: 100%;
`;

// Item de résultat de formation
const FormationResultItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  z-index: 200;
  position: relative;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

// Nom de la formation dans les résultats
const FormationName = styled.h2`
  font-size: ${props => props.small ? '16px' : '19px'};
  font-weight: 500;
  margin: 0 0 4px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-right: 20px;
  color: inherit;
`;

// Métadonnées de la formation
const FormationMeta = styled.div`
  color: inherit;
  font-size: 14px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  opacity: 0.8;
`;

// Conteneur des formations sélectionnées
const SelectedFormationsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 0;
`;


// Styled components for the selected formation display
const SelectedDisplayButton = styled(OptionButton)`
  margin-bottom: 10px;
  text-align: left;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  height: auto;
  padding: 12px 16px 4px 16px; // T12, R16, B4, L16
  gap: 0; // Ensure no gap affects alignment
`;

const SelectedDisplayFormationName = styled.h2`
  width: 100%;
  font-size: 16px;
  font-weight: 550;
  text-align: left !important;
  margin-bottom: 10px;
  line-height: 1.1;
  color: inherit;
  padding-right: 0 !important;
  flex: initial !important;
  margin-left: 0 !important;
`;

const SelectedDisplayFormationMeta = styled.div`
  width: 100%;
  font-size: 14px;
  text-align: left !important;
  margin-top: 0;
  line-height: 1.1;
  color: inherit;
  opacity: 0.8;
  padding-right: 0 !important;
  flex: initial !important;
  margin-left: 0 !important;
`;

const FormationOnboarding = () => {
  const navigate = useNavigate();
  const url = window.location.href;
  const modifyProfile = url.includes('profile/modify');
  const { isMobile } = useDeviceType();
  const { checkAuth } = useAuth();
  const searchInputRef = useRef(null);
  const resultsContainerRef = useRef(null);

  const [selectedFormation, setSelectedFormation] = useState(null);
  const [isCustomFormation, setIsCustomFormation] = useState(false);
  const [customFormationName, setCustomFormationName] = useState('');
  const [customFormationEtablissement, setCustomFormationEtablissement] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [text, setText] = useState('');
  const [isSubmitPressed, setIsSubmitPressed] = useState(false); // For submit button press
  const [onboardingChoice, setOnboardingChoice] = useState(() => {
    return localStorage.getItem('onboardingChoice'); // Initialize directly
  });
  const fullText = modifyProfile 
    ? "Dans quelle formation es-tu actuellement ?" 
    : "Dans quelle formation es-tu actuellement ?";
  
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  const [submitActive, setSubmitActive] = useState(false); // Added for submit button active state
  const [activeButton, setActiveButton] = useState(null); // For button press effect

  // Typing animation effect
  useEffect(() => {
    let currentIndex = 0;
    const charIncrement = 3;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex += charIncrement;
      } else {
        setText(fullText);
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 5);
    return () => clearInterval(typingInterval);
  }, [fullText, modifyProfile]);

  // Check if user already has formation data when modifying profile
  useEffect(() => {
    if (modifyProfile) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (userData.formation_id && !userData.personal_formation) { // Added check for personal_formation to avoid issues with old data structure
        // Try to find the matching formation in our data (using the imported formationsMeta)
        const existingFormation = Object.entries(formationsMeta).find( // Use imported formationsMeta
          ([id, formation]) => id === userData.formation_id
        );
        
        if (existingFormation) {
          setSelectedFormation({
            id: existingFormation[0],
            ...existingFormation[1]
          });
          setIsCustomFormation(false); // Ensure custom mode is off
        }
      } else if (userData.personal_formation) { // Check for custom formation name
        setIsCustomFormation(true);
        setSelectedFormation(null); // No Parcoursup formation selected
        setCustomFormationName(userData.personal_formation);
        setCustomFormationEtablissement(userData.current_establishment || '');
      }
    }
  }, [modifyProfile]); // formationsMeta (the import) is stable, so removed from deps

  // Search formations using inverted index when search text changes
  useEffect(() => {
    if (searchText.trim() === '') {
      setSearchResults([]);
      setShowResults(false); // Also hide results when search text is empty
      return;
    }
    
    // If searchIndex and formationsMeta are loaded (they are imported directly)
    // No need to check Object.keys().length > 0 if imported directly
    // `searchIndex` and `formationsMeta` now refer to the direct imports
    optimizedSearch(searchText, searchIndex, formationsMeta, ['formation'], 10)
      .then(results => {
        setSearchResults(results || []);
        setShowResults(results && results.length > 0);
      })
      .catch(error => {
        console.error("Error during Onboarding formation search:", error);
        setSearchResults([]);
        setShowResults(false);
      });
  }, [searchText]); // Removed searchIndex and formationsMeta from dependencies as they are direct imports

  // Handle click outside to close search results
  useEffect(() => { 
    const handleClickOutside = (event) => {
      const clickedInsideSearch = searchInputRef.current && searchInputRef.current.contains(event.target);
      const clickedInsideResults = resultsContainerRef.current && resultsContainerRef.current.contains(event.target);
      
      if (!clickedInsideSearch && !clickedInsideResults) {
        setShowResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle formation selection
  const handleFormationSelect = (formation) => {
    setSelectedFormation(formation);
    setSearchText('');
    setShowResults(false);
    setIsCustomFormation(false); // Selecting a formation from list turns off custom mode

    // Store selected Parcoursup formation name and clear custom ones
    localStorage.setItem('selected_formation_name', formation.nomFormation);
    localStorage.removeItem('personal_formation');
    localStorage.removeItem('current_establishment');
    // Store selected Parcoursup establishment name
    localStorage.setItem('selected_establishment_name', formation.nomEtablissement);
  };

  // Handle custom formation button click
  const handleCustomButtonClick = () => {
    if (isCustomFormation) { // If currently custom (blue button)
      setIsCustomFormation(false); // Turn off custom mode
      setCustomFormationName(''); // Clear custom input fields
      setCustomFormationEtablissement('');
      // No need to touch selected_formation_name here, as it's either null or will be overwritten by a new selection
    } else { // If not currently custom (normal button)
      setSelectedFormation(null); // Ensure no Parcoursup formation is selected
      setIsCustomFormation(true);  // Turn on custom mode
      // Clear Parcoursup-specific localStorage items
      localStorage.removeItem('selected_formation_name');
      localStorage.removeItem('formation_id');
      localStorage.removeItem('selected_establishment_name'); // Clear selected establishment name
    }
    setSearchText('');       // Clear search text from Parcoursup search
    setShowResults(false);   // Hide Parcoursup results
  };

  // Handle removing selected formation (Parcoursup or clearing custom inputs)
  const handleRemoveFormation = () => {
    setSelectedFormation(null);
    setIsCustomFormation(false);
    setCustomFormationName('');
    setCustomFormationEtablissement('');
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setShowResults(true);
  };

  // Handle focus on search input
  const handleSearchFocus = () => {
    setShowResults(true);
  };

  // Handle continue button click
  const handleActualSubmit = async () => {
    if (isTyping) return;
    if (!selectedFormation && (!isCustomFormation || !customFormationName.trim())) return;

    setIsLoading(true);
    
    let submissionData = {};
    if (isCustomFormation) {
      submissionData = {
        formation_id: '',
        personal_formation: customFormationName,
        current_establishment: customFormationEtablissement,
      };
      // Update localStorage for onboarding flow
      localStorage.setItem('formation_id', '');
      localStorage.setItem('personal_formation', customFormationName);
      localStorage.setItem('current_establishment', customFormationEtablissement);
    } else if (selectedFormation) {
      submissionData = {
        formation_id: selectedFormation.id,
        personal_formation: selectedFormation.nomFormation,
        current_establishment: selectedFormation.nomEtablissement,
      };
      // Update localStorage for onboarding flow
      localStorage.setItem('formation_id', selectedFormation.id);
      localStorage.setItem('personal_formation', selectedFormation.nomFormation);
      localStorage.setItem('current_establishment', selectedFormation.nomEtablissement);
    } else {
      setIsLoading(false);
      return; // Should not happen based on button enable logic
    }

    try {
      if (modifyProfile) {
        // Send the formation data to the backend
        await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', submissionData);
        
        // Update user data in localStorage
        let userData = JSON.parse(localStorage.getItem('user') || '{}');
        userData = { ...userData, ...submissionData };
        localStorage.setItem('user', JSON.stringify(userData));
        
        await checkAuth();
        navigate('/profile');
      } else {
        // Store formation info in localStorage for onboarding flow
        // This is now done above based on isCustomFormation or selectedFormation

        // Conditional navigation based on user choice
        const choice = localStorage.getItem('onboardingChoice');
        if (choice === 'cherche') {
          navigate('/problemes-formation');
        } else if (choice === 'trouve') {
          navigate('/year-study');
        } else {
          // Default fallback
          navigate('/problemes-formation');
        }
      }
    } catch (error) {
      console.error('Error saving formation data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (modifyProfile) {
      navigate('/profile');
    } else {
      navigate('/user-type-selection');
    }
  };

  // --- Submit Button Pointer Handlers ---
  const handleSubmitPressStart = () => {
    if (isLoading || isTyping || (!selectedFormation && (!isCustomFormation || !customFormationName.trim()))) return;
    setSubmitActive(true);
  };

  const handleSubmitPressEnd = () => {
    if (isLoading || isTyping || (!selectedFormation && (!isCustomFormation || !customFormationName.trim()))) return;
    setSubmitActive(false);
    // Call the actual submit logic, possibly with a delay like in UserTypeSelection
    // For now, calling directly. If a delay is needed, we can add setTimeout.
    // setTimeout(() => handleActualSubmit(), 150); // Example if delay is desired
    handleActualSubmit(); 
  };

  return (
    <Container>
      {isLoading && <LoadingOverlay isLoading={isLoading} />}
      <ReturnButton onClick={handleBack}>
        <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
      </ReturnButton>
      <ContentWrapper>
        {!modifyProfile && (
          onboardingChoice === 'trouve' ? (
            <ProgressBar startPercent={15} endPercent={45} />
          ) : (
            <ProgressBar startPercent={80} endPercent={85} />
          )
        )}
        <LogoBubbleWrapper>
          <LogoImage src={compassImage} alt="Compass Logo" />
          <ChatBubbleContainer isMobile={isMobile}>
            <ChatText isVisible={text.length > 0}>
              <span>{text}</span>
            </ChatText>
          </ChatBubbleContainer>
        </LogoBubbleWrapper>

        {/* SearchBox always visible */}
        <SearchBox>
          <div style={{ position: 'relative', width: '100%' }}>
            <SearchInputComponent
              inputRef={searchInputRef}
              placeholder="Rechercher une formation"
              value={searchText}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              disabled={isCustomFormation} // Remains disabled if custom mode is active
            />
            
            {/* FormationResultsContainer visibility depends on !isCustomFormation */}
            <FormationResultsContainer 
              ref={resultsContainerRef}
              visible={showResults && searchResults.length > 0 && !isCustomFormation}
            >
              {searchResults.map((formation, index) => (
                <FormationResultItem 
                  key={index} 
                  onClick={() => handleFormationSelect(formation)}
                >
                  <FormationName small>{formation.nomFormation}</FormationName>
                  <FormationMeta>
                    <span>{formation.nomEtablissement}</span>
                    {formation.commune && (
                      <span> • {formation.commune}</span>
                    )}
                  </FormationMeta>
                </FormationResultItem>
              ))}
              {searchResults.length === 0 && searchText.trim() !== '' && (
                <div style={{ padding: '16px', textAlign: 'center', color: '#888' }}>
                  Aucun résultat trouvé
                </div>
              )}
            </FormationResultsContainer>
          </div>
        </SearchBox>

        {/* Affichage de la formation sélectionnée (Parcoursup) */}
        {selectedFormation && !isCustomFormation && (
          <SelectedFormationsContainer>
            <SelectedDisplayButton
              $isSelected={true}
              type="button"
              aria-pressed={true}
              onClick={handleRemoveFormation} // Added to allow unselecting by clicking
            >
              <SelectedDisplayFormationName>
                {selectedFormation.nomFormation}
              </SelectedDisplayFormationName>
              {selectedFormation.nomEtablissement && (
                <SelectedDisplayFormationMeta>
                  <span>{selectedFormation.nomEtablissement}</span>
                </SelectedDisplayFormationMeta>
              )}
            </SelectedDisplayButton>
          </SelectedFormationsContainer>
        )}

        {/* Bouton "Ma formation n'est pas présente" */}
        {/* This button is visible if no Parcoursup formation is selected */}
        {!selectedFormation && (
          <OptionButton
            $isSelected={isCustomFormation}
            type="button"
            aria-pressed={isCustomFormation}
            onClick={handleCustomButtonClick} // Use the new handler
            onMouseDown={() => setActiveOption('custom')}
            onMouseUp={() => { setActiveOption(null); handleCustomButtonClick(); }} // Ensure correct handler is called
            onMouseLeave={() => setActiveOption(null)}
            onTouchStart={() => setActiveOption('custom')}
            onTouchEnd={() => { setActiveOption(null); handleCustomButtonClick(); }} // Ensure correct handler is called
            active={activeOption === 'custom'}
            style={{ marginTop: '10px' }}
          >
            Ma formation n'est pas présente
          </OptionButton>
        )}

        {/* Mode édition pour une formation personnalisée */}
        {/* Show if in custom mode AND no Parcoursup formation selected */}
        {isCustomFormation && !selectedFormation && (
          <div style={{ width: '100%', margin: '10px auto 0' }}>
            <input
              type="text"
              value={customFormationName}
              onChange={(e) => setCustomFormationName(e.target.value)}
              placeholder="Ma formation"
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                outline: 'none' // Added to remove focus border
              }}
            />
            <input
              type="text"
              value={customFormationEtablissement}
              onChange={(e) => setCustomFormationEtablissement(e.target.value)}
              placeholder="Mon école"
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                outline: 'none' // Added to remove focus border
              }}
            />
          </div>
        )}

        {/* Bouton de validation */}
        <SubmitButton
          type="button"
          disabled={isLoading || isTyping || (!selectedFormation && (!isCustomFormation || !customFormationName.trim()))}
          style={{
            cursor: (isLoading || isTyping || (!selectedFormation && (!isCustomFormation || !customFormationName.trim()))) ? 'not-allowed' : 'pointer',
            marginTop: '20px'
          }}
          onClick={(e) => { e.preventDefault(); /* handleSubmit(); is now handled by onMouseUp/onTouchEnd */ }}
          onMouseDown={handleSubmitPressStart}
          onMouseUp={handleSubmitPressEnd}
          onMouseLeave={() => setSubmitActive(false)} // Reset active state if mouse leaves button while pressed
          onTouchStart={handleSubmitPressStart}
          onTouchEnd={handleSubmitPressEnd}
          active={submitActive}
        >
          {isLoading ? 'Chargement...' : 'Continuer'}
        </SubmitButton>
      </ContentWrapper>
    </Container>
  );
};

export default FormationOnboarding;